import { useState, useEffect } from "react";
import {
  getBioContent,
  updateBioContent,
} from "../firebase/firebase-functions";

const FALLBACK_BIO = {
  bioPage:
    "I'm a singer/songwriter with a passport full of stories. Born in Texas, raised in Nigeria, shaped by London, and now chasing my music dreams in New York City. Growing up all over the world, I discovered the power of music as a constant source of comfort and connection, a way of feeling at home no matter where I was. My sound blends pop, R&B, and folk-inspired country melodies, rooted in everything I've seen, felt, and lived. I'm just getting started, and I'm so excited to share the journey with you.",
  homePageBioOne:
    "Born in Texas, raised in Nigeria, shaped by London, and now chasing music dreams in New York City. A 23-year-old artist with a passport full of stories and a sound that blends pop, R&B, and folk-inspired melodies.",
  homePageBioTwo:
    "My sound blends pop, R&B, and folk-inspired country melodies, rooted in everything I've seen, felt, and lived. I'm just getting started, and I'm so excited to share the journey with you.",
};

const BioManagement = () => {
  const [bioData, setBioData] = useState(FALLBACK_BIO);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBio();
  }, []);

  const fetchBio = async () => {
    setIsLoading(true);
    const result = await getBioContent();
    if (result.success && result.bio) {
      setBioData({
        bioPage: result.bio.bioPage || FALLBACK_BIO.bioPage,
        homePageBioOne:
          result.bio.homePageBioOne || FALLBACK_BIO.homePageBioOne,
        homePageBioTwo:
          result.bio.homePageBioTwo || FALLBACK_BIO.homePageBioTwo,
      });
    }
    setIsLoading(false);
  };

  const handleSave = async () => {
    if (
      !bioData.bioPage.trim() ||
      !bioData.homePageBioOne.trim() ||
      !bioData.homePageBioTwo.trim()
    ) {
      setError("All bio fields are required.");
      return;
    }

    setIsSaving(true);
    setError("");
    setSuccessMessage("");

    const result = await updateBioContent(bioData);

    if (result.success) {
      setSuccessMessage("Bio updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } else {
      setError(result.error || "Failed to update bio.");
    }

    setIsSaving(false);
  };

  const handleChange = (field, value) => {
    setBioData((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="loader"></div>
        <p>Loading bio...</p>
      </div>
    );
  }

  return (
    <div className="bio-management">
      <div className="bio-field-group">
        <label className="bio-field-label">
          Bio Page — Full Biography
          <span className="bio-field-hint">
            Shown on Bio page. Can be long.
          </span>
        </label>
        <textarea
          className="bio-textarea"
          value={bioData.bioPage}
          onChange={(e) => handleChange("bioPage", e.target.value)}
          rows={6}
          disabled={isSaving}
          placeholder="Full biography text..."
        />
        <span className="bio-char-count">{bioData.bioPage.length} chars</span>
      </div>

      <div className="bio-field-group">
        <label className="bio-field-label">
          Home Page — Hero Description
          <span className="bio-field-hint">
            Short intro shown in the hero section on the home page.
          </span>
        </label>
        <textarea
          className="bio-textarea"
          value={bioData.homePageBioOne}
          onChange={(e) => handleChange("homePageBioOne", e.target.value)}
          rows={3}
          disabled={isSaving}
          placeholder="Short hero bio..."
        />
        <span className="bio-char-count">
          {bioData.homePageBioOne.length} chars
        </span>
      </div>

      <div className="bio-field-group">
        <label className="bio-field-label">
          Home Page — About Section
          <span className="bio-field-hint">
            Shown in the "About Anjali" section lower on the home page.
          </span>
        </label>
        <textarea
          className="bio-textarea"
          value={bioData.homePageBioTwo}
          onChange={(e) => handleChange("homePageBioTwo", e.target.value)}
          rows={3}
          disabled={isSaving}
          placeholder="About section bio..."
        />
        <span className="bio-char-count">
          {bioData.homePageBioTwo.length} chars
        </span>
      </div>

      {error && <p className="error-message">{error}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}

      <div className="bio-actions">
        <button
          className="btn-primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save Bio"}
        </button>
        <button
          className="btn-secondary"
          onClick={fetchBio}
          disabled={isSaving}
        >
          Reset Changes
        </button>
      </div>
    </div>
  );
};

export default BioManagement;
